import React from 'react'
import ReactTable from 'react-table'

import { RatedEntityCell, ratedEntityFilter } from './RatedEntityCell'
import { RatingsHistory } from './RatingsHistory'
import { RatingsCell, ratingsFilter } from './RatingsCell'
import { EntityDetailsCell, entityDetailsFilter } from './EntityDetailsCell'
import { IdentifiersCell, identifiersFilter } from './IdentifiersCell'
import { HeaderCell } from './HeaderCell'
import { RatablesFilter } from './RatablesFilter'
import { RatablesSort } from './RatablesSort'
import {
  ColumnIDs,
  haveXGSRatings,
  RatablesMarketingProps,
  RatablesSortByTypes
} from './Ratables'

export type SearchRatablesRatingItemProps = {
  filterRatingType: string
  filterNationalRatingType: string
  filterInvestmentGradeType: string
  ratingId: string
  ratingEffectiveDate: Date
  ratingCode: string
  ratingActionDescription: string
  ratingType: string
  recoveryRatingValue: string
  ratingAlertCode: string
}

export type SearchRatablesRatingProps = {
  id?: string
  country?: string
  ratingActionDescription?: string
  ratingTypeDescription?: string
  ratingAlertCode?: string | null
  ratableName?: string
  ratingTypeId?: number
  ratingLocalActionDescription?: string
  ratingAlertDescription?: string
  ratingCode?: string
  ratingLocalValue?: string
  ratingEffectiveDate?: string
  orangeDisplay?: boolean
  originalDate?: string
  solicitFlag?: string | null
  countryId?: number
  latestFlag?: boolean
}

export type SearchRatablesDisclosureProps = {
  regulatoryAgency: string
  status: string
}

export type SearchRatableTransactionProps = {
  description: string | null
}

export type SearchRatableItemProps = {
  class: string
  subClass: string
  permalink: string
  issueName: string
  issuer: string
  ratingDate: string
  maturityDate: string
  cusip: string[] | null[]
  isin: string[] | null[]
  originalAmount: number
  currency: string
  couponRate: string
  disclosures: SearchRatablesDisclosureProps[]
  marketing: RatablesMarketingProps
  transaction: SearchRatableTransactionProps
  ratings: any[]
}

export type SearchRatablesProps = {
  items: SearchRatableItemProps[]
  printView: boolean
  defaultPageSize: number
}

interface SearchRatablesState {
  data: any[]
  displayLocalValues: boolean
  expanded: any[]
  originalData: any[]
  sorted: any[]
}

export class SearchRatables extends React.Component<
  SearchRatablesProps,
  SearchRatablesState
> {
  state = {
    data: [],
    displayLocalValues: false,
    expanded: [],
    originalData: [],
    sorted: []
  }

  componentDidMount() {
    const { items } = this.props
    const data = this.createData(items)
    const originalData = [...data]
    this.setState({ data, originalData })
  }

  // eslint-disable-next-line class-methods-use-this
  createData = (items: any) =>
    items.map((item: any) => {
      const {
        cusip,
        isin,
        marketing = {
          countries: [],
          sectors: [],
          analysts: []
        },
        transaction
      } = item
      const { countries = [], sectors = [], analysts = [] } = marketing || {}
      const [country = {}] = countries || []
      const [sector = {}] = sectors || []
      const cusips = cusip.map((cusipItem: string) => ({
        type: 'CUSIP',
        value: cusipItem
      }))

      const isins = isin.map((isinItem: string) => ({
        type: 'ISIN',
        value: isinItem
      }))

      let debtTypeSubDebtTypeValue = ''
      if (item.subgroupName) {
        debtTypeSubDebtTypeValue = debtTypeSubDebtTypeValue.concat(
          item.subgroupName
        )
      }

      if (item.ratableTypeDescription) {
        if (debtTypeSubDebtTypeValue) {
          debtTypeSubDebtTypeValue = debtTypeSubDebtTypeValue
            .concat('; ')
            .concat(item.ratableTypeDescription)
        } else if (debtTypeSubDebtTypeValue === '') {
          debtTypeSubDebtTypeValue = debtTypeSubDebtTypeValue.concat(
            item.ratableTypeDescription
          )
        }
      }

      const debtTypeSubDebtType = [
        {
          type: 'debtTypeSubDebtType',
          value: debtTypeSubDebtTypeValue
        }
      ]

      let identifiers = [{}]
      if (debtTypeSubDebtType) {
        identifiers = identifiers.concat(debtTypeSubDebtType)
      }

      if (cusips) {
        identifiers = identifiers.concat(cusips)
      }

      if (isins) {
        identifiers = identifiers.concat(isins)
      }

      const ratableRatings = item.ratings.map((rating: any) => ({
        ratingType: rating.ratingTypeDescription,
        changeDate: rating.ratingChangeDate,
        ...rating
      }))

      return {
        ...item,
        transaction: transaction.description,
        ratableRatings,
        analysts,
        country,
        identifiers,
        sector
      }
    })

  handleFilterClick = (selectedFilters: string[]) => {
    const { originalData } = this.state

    const result =
      selectedFilters.length > 0
        ? originalData.filter((item: SearchRatableItemProps) =>
            item.ratings.some(
              (rating: SearchRatablesRatingItemProps) =>
                selectedFilters.includes(rating.filterRatingType) ||
                selectedFilters.includes(rating.filterInvestmentGradeType) ||
                selectedFilters.includes(rating.filterNationalRatingType)
            )
          )
        : originalData

    this.setState({ data: result })
  }

  handleRatingHistoryClick = (rowIndex: number) => {
    const { expanded } = this.state
    const newExpanded = []
    for (let i = 0; i <= rowIndex; i += 1) {
      if (i === rowIndex) newExpanded[i] = true
      if (expanded[i]) newExpanded[i] = false
    }
    this.setState({ expanded: newExpanded })
  }

  handleSortClick = (sortType: string) => {
    if (sortType === RatablesSortByTypes.MOST_RECENT) {
      this.setState({ sorted: [{ id: ColumnIDs.RATINGS, desc: true }] })
    } else if (sortType === RatablesSortByTypes.DEBT_TYPE) {
      this.setState({ sorted: [{ id: ColumnIDs.IDENTIFIERS, desc: false }] })
    }
  }

  createColumns(displayLocalValues: boolean) {
    const { printView } = this.props

    const { data } = this.state
    const ratablesData = data

    const foundXGSRatings = haveXGSRatings(ratablesData)
    let colWidth
    if (!printView) {
      colWidth = foundXGSRatings ? 170 : 225
    }

    const ratedEntity = {
      id: ColumnIDs.RATED_ENTITY,
      width: colWidth,
      Header: () => <HeaderCell heading="Rated Entity / Debt" />,
      filterMethod: (filterItem: any, data: any) =>
        ratedEntityFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatedEntityCell cellProps={cellProps} printView={printView} />
      )
    }

    const ratings = {
      id: ColumnIDs.RATINGS,
      width: colWidth,
      accessor: (data: any) =>
        data.ratableRatings && data.ratableRatings.length
          ? data.ratableRatings[0].ratingEffectiveDate
          : null,
      Header: () => <HeaderCell heading="Ratings" />,
      filterMethod: (filterItem: any, data: any) =>
        ratingsFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatingsCell
          cellProps={cellProps}
          handleClick={this.handleRatingHistoryClick}
          showRatingHistory={false}
          displayLocalValues={displayLocalValues}
          isActive
        />
      )
    }

    const xgsRatings = {
      id: ColumnIDs.RATINGS_XGS,
      width: colWidth,
      accessor: (data: any) =>
        data.ratableRatings && data.ratableRatings.length
          ? data.ratableRatings[0].ratingEffectiveDate
          : null,
      Header: () => <HeaderCell heading="Ratings (xgs)" />,
      filterMethod: (filterItem: any, data: any) =>
        ratingsFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatingsCell
          cellProps={cellProps}
          handleClick={this.handleRatingHistoryClick}
          showRatingHistory={false}
          displayLocalValues={displayLocalValues}
          isActive
          isXGSCell
        />
      )
    }

    const entityDetails = {
      id: ColumnIDs.ENTITY_DETAILS,
      width: colWidth,
      Header: () => <HeaderCell heading="Entity Details" />,
      filterMethod: (filterItem: any, data: any) =>
        entityDetailsFilter(filterItem, data),
      Cell: (cellProps: any) => <EntityDetailsCell cellProps={cellProps} />
    }

    const identifiers = {
      id: ColumnIDs.IDENTIFIERS,
      maxWidth: colWidth,
      accessor: (data: any) => {
        if (data.subgroupName && data.ratableTypeDescription) {
          return data.subgroupName.concat(data.ratableTypeDescription)
        }
        if (data.subgroupName) {
          return data.subgroupName
        }
        return null
      },
      Header: () => <HeaderCell heading="Debt Type & Identifiers" />,
      filterMethod: (filterItem: any, data: any) =>
        identifiersFilter(filterItem, data),
      Cell: (cellProps: any) => <IdentifiersCell cellProps={cellProps} />
    }

    const hiddenExpander = {
      id: 'hiddenExpander',
      expander: true,
      Header: () => null,
      Cell: () => null,
      Expander: () => null,
      width: 0
    }

    if (foundXGSRatings) {
      return [
        ratedEntity,
        ratings,
        xgsRatings,
        entityDetails,
        identifiers,
        hiddenExpander
      ]
    }

    return [ratedEntity, ratings, entityDetails, identifiers, hiddenExpander]
  }

  render() {
    const { data, expanded, sorted, displayLocalValues } = this.state
    const { printView, defaultPageSize } = this.props
    const columns = this.createColumns(displayLocalValues)
    const mainClass = printView
      ? ''
      : 'frw-page-layout frw-page-layout--2__left-main'
    return (
      <div className={mainClass}>
        {printView ? null : (
          <div
            className="frw-column__left"
            style={{
              marginTop: '1rem',
              marginLeft: '-1rem'
            }}
          >
            <RatablesFilter handleFilterClick={this.handleFilterClick} />
            <RatablesSort handleClick={this.handleSortClick} />
          </div>
        )}
        <div className="frw-column__main">
          <div className="frw-ReactTable">
            <ReactTable
              columns={columns}
              data={data}
              defaultPageSize={defaultPageSize}
              expanded={expanded}
              minRows={0}
              showPagination={false}
              sortable={!printView}
              sorted={sorted}
              onExpandedChange={(newExpanded) =>
                this.setState({ expanded: newExpanded })
              }
              onSortedChange={(newSorted) =>
                this.setState({ sorted: newSorted })
              }
              SubComponent={(row) => <RatingsHistory row={row} />}
            />
          </div>
        </div>
      </div>
    )
  }
}
export default SearchRatables
